import Vuex from 'vuex';

import Domain from 'helpers/Domain';
import ProfileStore from 'apps/userProfile/stores/modules/ProfileStore';
import Feature, {
    CAMPAIGN_SERVICE_ENABLED,
    SHOWTIME_BANNER_ENABLED,
} from 'aa/helpers/featureConstants';
import FlowStore from 'aa/vue/stores/modules/FlowStore';
import FtcStore from 'aa/vue/stores/modules/FtcStore';
import PaymentStore from 'aa/vue/stores/modules/PaymentStore';
import TrackingStore from 'aa/vue/stores/modules/TrackingStore';
import PlanStore from 'aa/vue/stores/modules/PlanStore';
import MultiSubPlanStore from 'aa/vue/stores/modules/MultiSubPlanStore';
import CouponPreviewStore from 'aa/vue/stores/modules/CouponPreviewStore';
import PageAttributesStore from 'aa/vue/stores/modules/PageAttributesStore';
import ShowsPickerStore from 'apps/showsPicker/stores/ShowsPickerStore';
import UserStore from 'aa/vue/stores/modules/UserStore';
import VariantStore from 'aa/vue/stores/modules/VariantStore';
import ModalStore from 'aa/vue/stores/modules/ModalStore';
import FeatureFlagsStore from 'apps/featureFlags/stores/FeatureFlagsStore';
import ParentalControlsStore from 'vue/stores/modules/ParentalControlsStore';
import AccountSwitchPlanStore from 'aa/vue/stores/account/switchPlan/AccountSwitchPlanStore';

// CBSCOMSUB-4696
import NotificationBarStore from 'aa/vue/stores/modules/NotificationBarStore';
import PaymentService from 'aa/vue/services/PaymentService';
import PartnerStore from './modules/Partner/PartnerStore';
// remove when the CAPTCHA EPIC is completed
import AuthSuiteStore from 'vue/stores/modules/AuthSuite/AuthSuiteStore';
import { HOME_URL } from 'aa/vue/constants/aaRoutes';
import SheerIDStore from 'aa/vue/stores/modules/SheerIDStore';
//
import Product from 'aa/vue/models/Product';
import { merge } from 'lodash';
import StoredPaymentMethod from './modules/StoredPaymentMethod';

export default function () {
    const serverData = document.getElementById('app-config')
        ? JSON.parse(document.getElementById('app-config').innerHTML)
        : false;
    return new Vuex.Store({
        state: {
            count: 0,
            token: null,
            promo: null,
            isCMSPromo: false,
            navFrom: null,
            imageHost: null,
            featureFlags: {},
            localeDictionary: {},
            userMessage: {},
            serverData: {}, // does this need to be here?
            autoPromoChecked: false,
            branchJourneyKey: '',
        },

        mutations: {
            setToken(state, token) {
                state.token = token;
            },
            setPromo(state, promo) {
                state.promo = promo;
            },
            setIsCMSPromo(state, isCMSPromo) {
                state.isCMSPromo = isCMSPromo;
            },
            setAutoPromoChecked(state, hasBeenChecked) {
                state.autoPromoChecked = hasBeenChecked;
            },
            setNavFrom(state, from) {
                state.navFrom = from;
            },
            setImageHost(state, imageHost) {
                state.imageHost = imageHost;
            },
            setFeatureFlags(state, featureFlags) {
                if (featureFlags && typeof featureFlags === 'object') {
                    state.featureFlags = featureFlags;
                }
            },
            setLocaleDictionary(state, localeDictionary) {
                state.localeDictionary = localeDictionary;
            },
            setServerData(state, payload) {
                state.serverData = payload;
            },
            mergeServerData(state, payload) {
                let newServerData = Object.assign({}, state.serverData);
                for (let prop in state.serverData) {
                    if (payload.hasOwnProperty(prop)) {
                        newServerData[prop] = {
                            ...newServerData[prop],
                            ...payload[prop],
                        };
                    }
                }

                state.serverData = newServerData;
            },
            mergeServerDataWithDeepAssign(state, payload) {
                merge(state.serverData, payload);
            },
            addObjectToServerData(state, payload) {
                let newServerData = Object.assign({}, state.serverData);

                for (let prop in payload) {
                    newServerData[prop] = {
                        ...payload[prop],
                    };
                }

                state.serverData = newServerData;
            },
            setUserMessage(state, payload) {
                state.userMessage = payload;
            },
            setBranchJourneyKey(state, branchKey) {
                state.branchJourneyKey = branchKey;
            },
            setCampaignOfferCoupon(state, noPromo) {
                if (CBS.Registry.Features[CAMPAIGN_SERVICE_ENABLED]) {
                    let campaignPromo = state.plan?.selectedPlan?.offerCouponCode;

                    if (noPromo) {
                        state.promo = null;
                    } else {
                        // if no campaign promo, fallback to param promo
                        state.promo = campaignPromo || state.promo || null;
                    }
                }
            },
        },

        getters: {
            getLocaleDictionary: (state) => state.localeDictionary,

            cmsImageBaseURL: (state) => {
                return state.imageHost + '/base/';
            },

            // CBSCOMSUB-4696
            getServerDataByKey: (state) => (key) => {
                if (!state.serverData) return null;

                if (key in state.serverData) {
                    return state.serverData[key];
                }

                return null;
            },

            featureIsActive: (state) => (feature) => {
                return state.featureFlags?.[feature] === true;
            },

            canBundleShowtime: (state, getters) => {
                return Boolean(
                    getters.featureIsActive('showtime_bundle') && !getters['flow/isEdu'],
                );
            },

            /**
             * @param state
             * @param getters
             * @returns {*|boolean}
             */
            bundleShowtime: (state, getters) => {
                if (CBS.Registry.Features.showtime_integration_enabled === true) {
                    return false;
                }
                return Boolean(getters.canBundleShowtime && state.plan.addonShowtime);
            },
            getProductParamsForTracking: (state, getters) => {
                const plan = getters['plan/getSelectedPlan'];

                if (plan) {
                    let trackingParams = {
                        purchaseProductName: plan.titleForTracking,
                        productOfferPeriod: plan?.trialString ? plan.trialString : '0',
                        productPricingPlan: plan.planType,
                        purchasePrice: plan.rawPrice,
                        partnerBundle: plan.addOnCodes?.[0] ?? '',
                        pickPlanType: plan.planTitle,
                        showtimeBundleRawPrice: plan.showtimeBundleRawPrice,
                        pickPlanSku: plan.recurlyCode,
                        promoOffer: getters['plan/getPromoOfferStringForTracking'],
                        productDiscount: plan?.couponString,
                        purchasePromoCode: plan?.couponDiscount?.code,
                    };

                    return trackingParams;
                }

                return {};
            },

            getPaymentPageProductParamsForTracking: (state, getters) => {
                const plan = getters['plan/getSelectedPlan'];

                if (plan) {
                    let trackingParamsPaymentPage = {
                        purchaseEventBillingStart: '1',
                        purchaseProduct: plan.recurlyCode,
                        purchaseQuantity: '1',
                        purchaseCategory: plan.titleForTracking,
                    };

                    return Object.assign(
                        getters.getProductParamsForTracking,
                        trackingParamsPaymentPage,
                    );
                }
                return {};
            },

            getRedirectUrlForResellers: (state, getters) => (userState) => {
                let userData = userState ? userState : state.user;
                let source = userData.svod.user_package.source,
                    status = userData.svod.user_package.status,
                    isCancelled = status === 'ACTIVE_CANCELLED',
                    partner = getters['flow/type'],
                    partnersWithMessage = ['walmartplus'];

                if (partnersWithMessage.includes(partner) && source !== partner) {
                    return isCancelled
                        ? '/account/'
                        : '/account/?notification=partner-subscribed&partner=' + partner + '';
                } else {
                    return `${CBS.Registry.region.prefix || ''}${HOME_URL}`;
                }
            },

            getTrackingParamsForGift: (state) => {
                const args = {};

                if (state.user.isExSubscriber || state.user.isSubscriber) {
                    args.giftCardRedeemExistingSub = '1';
                    args.pageType = 'svod_gift';
                    args.userType = state.user.svod.status;
                } else {
                    args.userRegType = 'gift';
                }

                return args;
            },

            getTrackingParamsForMVPDPartner: (state) => {
                return {
                    authPartnerId: state.authSuite?.selectedProviderName,
                    mvpdPartnerId: state.authSuite?.selectedProviderCode,
                    mvpdPartnerName: state.authSuite?.selectedProviderName,
                };
            },

            getTrackingParamsForSignUp: (state, getters) => {
                const args = {
                    pageType: 'svod_signup',
                };

                Object.assign(args, getters['flow/getTrackingActivationInfo']);

                if (!Domain.isDomestic()) {
                    if (state?.partner?.subscriptionDetails?.partnerCode) {
                        args.activationShortCode = state.partner.subscriptionDetails.partnerCode;
                    }
                }

                if (getters['flow/isGift']) {
                    Object.assign(args, getters['getTrackingParamsForGift']);
                }

                if (state.authSuite?.mvpdLoginEventBound) {
                    Object.assign(args, getters['getTrackingParamsForMVPDPartner']);
                }

                return args;
            },

            isShowtimeBannerEnabled: (state, getters) => {
                return getters['featureIsActive'](SHOWTIME_BANNER_ENABLED) === true;
            },
            getBranchJourneyKey(state) {
                return state.branchJourneyKey;
            },

            isPartnerBundle: (state, getters) => {
                return Feature.partnerBundleEnabled() && getters['flow/isCatan'];
            },
        },

        actions: {
            loadServerData({ dispatch, commit }, json) {
                if (json.flow) {
                    dispatch('flow/loadStore', json.flow);
                }

                dispatch('pageAttributes/setPageAttributes', json.pageAttributes || {});

                if (!json.multiSubPlanPicker && json?.flow?.forceAnnual) {
                    // plan type - force annual to show up first if flow says so
                    dispatch('plan/setPlanType', {
                        planType: Product.TYPE_ANNUAL,
                    });
                }

                if (json.variant) {
                    dispatch('variant/setVariant', json.variant);
                }

                if (json.plans && !json.multiSubPlanPicker) {
                    dispatch('plan/setPlans', json.plans);
                }

                commit('setFeatureFlags', json.featureFlag);
                commit('user/parseUserData', json.user);
                commit('setServerData', json);

                // PPIWEBM-2951: hydrate the server-injected coupon preview (INTL, FF-gated)
                dispatch('couponPreview/loadServerData');

                // load AuthSuite if enabled
                const authSuiteEnabled = json.authSuiteConfig?.enabled;
                if (authSuiteEnabled) {
                    const authSuiteConfig = {
                        ...json.authSuiteConfig,
                        regionBaseUrl: json.regionBaseUrl,
                    };
                    dispatch('authSuite/loadAuthSuite', authSuiteConfig);
                }

                if (json.user?.isLoggedIn) {
                    dispatch('flow/didCompleteSignIn');
                }

                if (json.featureFlag?.aa_purchase && !json.multiSubPlanPicker) {
                    let planTypeFromSession = sessionStorage.getItem('planType');
                    try {
                        planTypeFromSession =
                            JSON.parse(planTypeFromSession) || Product.TYPE_MONTHLY;
                    } catch (e) {
                        planTypeFromSession = Product.TYPE_MONTHLY;
                    }
                    if (json?.pageAttributes?.default_to_annual) {
                        planTypeFromSession = Product.TYPE_ANNUAL;
                    }

                    dispatch('plan/loadStore', {
                        planType: planTypeFromSession,
                        plans: json.plans,
                    });
                }

                if (json?.recurlyPublicKey) {
                    dispatch('payment/loadStore', {
                        recurlyPublicKey: json.recurlyPublicKey,
                    });
                }

                if (json.multiSubPlanPicker && json.plans) {
                    // Change to setPlans when backend has been set up
                    dispatch('plan/setPlans', json.plans);
                }

                if (json.multiSubPlanPicker && json.plans) {
                    dispatch('plan/setAttributes', json.multiSubPlanAttributes);
                }
            },
            getPromo({ commit, rootState }) {
                return new Promise((resolve, reject) => {
                    PaymentService.getPromo(rootState.user.statusCode)
                        .then((result) => {
                            const promo = result?.promo_code;
                            const isCMSPromo = result?.isCMSPromo ?? false;
                            if (promo) {
                                commit('setPromo', promo);
                                commit('setIsCMSPromo', isCMSPromo);
                            }
                            resolve();
                        })
                        .catch((err) => {
                            reject(err);
                        });
                });
            },
            async updateCoupon({ commit }, couponCode) {
                if (typeof couponCode === 'string') {
                    couponCode = couponCode.trim();
                }

                commit('setPromo', couponCode);
            },
            // use syncServerDataWithDeepAssign for sync that has reactivity issues
            async syncServerData({ commit, dispatch }, payload) {
                commit('mergeServerData', payload);
                await dispatch('syncServerDataInModules');
            },
            async syncServerDataInModules({ state, dispatch }) {
                await dispatch('user/loadServerData');
                await dispatch('notification/loadServerData');
                await dispatch('couponPreview/loadServerData');
            },
            async syncServerDataWithDeepAssign({ commit, dispatch }, payload) {
                commit('mergeServerDataWithDeepAssign', payload);
                await dispatch('syncServerDataInModules');
            },
        },

        // namespaces defined here
        modules: {
            authSuite: AuthSuiteStore,
            ftc: FtcStore,
            user: UserStore,
            profile: ProfileStore,
            payment: PaymentStore,
            tracking: TrackingStore,
            flow: FlowStore,
            pageAttributes: PageAttributesStore,
            plan: serverData.multiSubPlanPicker ? MultiSubPlanStore : PlanStore,
            couponPreview: CouponPreviewStore,
            AccountSwitchPlan: AccountSwitchPlanStore,
            showspicker: ShowsPickerStore,
            variant: VariantStore,
            partner: PartnerStore,
            parentalControls: ParentalControlsStore,
            // CBSCOMSUB-4696
            notification: NotificationBarStore,
            modal: ModalStore,
            featureFlags: FeatureFlagsStore,
            sheerid: SheerIDStore,
            storedPaymentMethod: StoredPaymentMethod,
        },
    });
}
